import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
// @mui
import { alpha } from "@mui/material/styles";
import {
  Box,
  Divider,
  Typography,
  Stack,
  MenuItem,
  Avatar,
  IconButton,
  Popover,
} from "@mui/material";
// mocks_
import { logout, selectUser } from "../../../redux/slices/auth.slice";
import { trimEmail } from "../../../utils/formatString";

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const [open, setOpen] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const isAdmin = user?.roles?.some((role) => {
    const roleName = (role?.name || "").toLowerCase();
    return roleName === "admin" || roleName === "superadmin";
  });

  const menuOptions = [
    {
      label: "Home",
      icon: "eva:home-fill",
      path: "/dashboard/app",
    },
    isAdmin && {
      label: "Settings",
      icon: "eva:settings-2-fill",
      path: "/dashboard/settings",
    },
  ].filter(Boolean);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    await setOpen(null);
    navigate("/login");
    dispatch(logout());
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("organizationId");
    window.location.reload();
  };

  const displayName =
    (user?.firstName && `${user?.firstName} ${user?.lastName}`) ??
    trimEmail(user?.email) ??
    "Guest";

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            "&:before": {
              zIndex: 1,
              content: "''",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              position: "absolute",
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
        data-testid="account-popover"
      >
        <Avatar src={user?.avatarURL} alt="photoURL">
          {displayName?.[0]?.toUpperCase()}
        </Avatar>
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 0,
            mt: 1.5,
            ml: 0.75,
            width: 180,
            "& .MuiMenuItem-root": {
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {(user?.firstName && `${user?.firstName} ${user?.lastName}`) ??
              user?.email ??
              trimEmail(user?.email) ??
              "Guest"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {user?.roles?.map((role) => role?.name).join(", ")}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Stack sx={{ p: 1 }}>
          {menuOptions.map((option) => (
            <MenuItem
              key={option.label}
              onClick={() => {
                handleClose();
                if (option.path) {
                  navigate(option.path);
                }
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: "dashed" }} />

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          Logout
        </MenuItem>
      </Popover>
    </>
  );
}
